import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ReadOnlyBanner } from "@/components/common/ReadOnlyBanner";
import { Button } from "@/components/design/Button";
import { ChallengeStep } from "./ChallengeStep";
import { CompanyPoolStep } from "./CompanyPoolStep";
import { FloatingActionRow } from "./FloatingActionRow";
import { MatchSettingsModal } from "./MatchModals";
import { PeopleStep } from "./PeopleStep";
import { ResultStep } from "./ResultStep";
import { SaveStatus } from "./SaveStatus";
import type { SlideId, StepId } from "./match-design-types";
import type { UseMatchEditorResult } from "./use-match-editor";
import { STEPS, nextStepId, prevStepId, stepIndex } from "./match-design-rules";
import {
  UNKNOWN_MATCH_MODE,
  assertUnhandledMode,
  describesRole,
  evaluatesFixedPool,
  matchModeLabelId,
  matchSubject,
  poolStepLabelId,
  resolveMatchMode,
  type UiMatchMode,
} from "./match-mode";

function stepLabelId(id: StepId, mode: UiMatchMode): string {
  switch (id) {
    case "challenge":
      return "match.step.challenge";
    case "people":
      // Rank ranks individual candidates, not a team, and a company pool holds companies
      // (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4). The isRank boolean this
      // replaced could carry exactly two namings; the reasoning is in match-mode.ts.
      return poolStepLabelId(mode);
    case "result":
      return "match.step.result";
  }
}

interface MatchWorkflowProps {
  editor: UseMatchEditorResult;
  onNavigate?: (slide: SlideId) => void;
}

export function MatchWorkflow({
  editor,
  onNavigate: onNavigateExternal,
}: MatchWorkflowProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data, stepId, setStepId, update, rename, flush, discard, myRole } = editor;
  // Matches are created fully-named via NewMatchModal (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4, revised), so
  // the settings dialog is only ever an edit surface reached through the gear.
  const [settingsOpen, setSettingsOpen] = useState(false);
  function closeSettings() {
    setSettingsOpen(false);
  }
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const canDelete = editor.actions?.canDelete === true;
  const onNavigate = (slide: SlideId) => {
    if (onNavigateExternal) onNavigateExternal(slide);
    else navigate(slide === "share" ? "/share" : "/match");
  };

  const isReview = editor.solverResult !== null;
  const stepIdx = stepIndex(stepId);
  // Modus ist ab Erstellung immutabel (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4): er kommt aus der Match.matchMode-Spalte
  // (genesis #159 / V0035) und wird nur noch angezeigt, nicht mehr umgeschaltet.
  // resolveMatchMode: null (theoretischer Altbestand) -> OPTIMIZE, unbekanntes Token -> UNKNOWN.
  const mode = resolveMatchMode(data.matchMode);
  // Beim Wiederoeffnen laedt data async: assessOnly dem persistierten Modus nachziehen,
  // sonst liefe der Solver eines Ranken-Matches im Optimize-Pfad.
  // Whose pool the second step fills. The company arm has its own screen for it, and the fork sits
  // here rather than inside PeopleStep because that screen is a person finder by definition — the
  // question "which kind of thing does this pool hold" is answered once, where the mode is
  // resolved. (The RESULT step forks inside ResultStep; see the comment at its call site.)
  const subject = matchSubject(mode);
  const assessFromMode = evaluatesFixedPool(mode);
  const { setAssessOnly } = editor;
  useEffect(() => {
    setAssessOnly(assessFromMode);
  }, [assessFromMode, setAssessOnly]);

  function next() {
    const nxt = nextStepId(stepId);
    if (nxt) setStepId(nxt);
  }
  function prev() {
    const prv = prevStepId(stepId);
    if (prv) setStepId(prv);
  }
  async function handleDelete() {
    setConfirmDeleteOpen(false);
    await discard();
  }

  // Extracted so the result step can host it inside ResultStep's own floating
  // row: two sticky rows at the same offset line up while scrolling but drift
  // apart once both settle into flow at the bottom of the page.
  const backButton =
    stepIdx > 0 ? (
      // The secondary variant is transparent by design; floating over scrolling
      // list content that reads as a rendering bug (rows shine through the
      // label). The page-colored pill behind it keeps the button opaque without
      // touching the shared variant.
      <span className="pointer-events-auto rounded-full bg-page shadow-xl">
        <Button
          variant="secondary"
          onClick={prev}
          title={intl.formatMessage(
            {
              id: "match.workflow.prevTo",
              defaultMessage: "Zurück zu {label}",
            },
            {
              label: STEPS[stepIdx - 1]
                ? intl.formatMessage({ id: stepLabelId(STEPS[stepIdx - 1]!.id, mode) })
                : "",
            },
          )}
          className="tracking-[0.2em]"
        >
          ← {STEPS[stepIdx - 1]?.n}{" "}
          {STEPS[stepIdx - 1] ? (
            <FormattedMessage id={stepLabelId(STEPS[stepIdx - 1]!.id, mode)} />
          ) : null}
        </Button>
      </span>
    ) : null;

  return (
    <div id="match-workflow" className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-3">
        <BackButton />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!editor.actions?.canUpdatePrepare ? (
          <div className="font-serif text-smycles-display-s leading-none">
            {data.matchTitle ||
              intl.formatMessage({ id: "match.unnamedMatch", defaultMessage: "Unbenanntes Match" })}
          </div>
        ) : (
          <input
            value={data.matchTitle ?? ""}
            onChange={(e) => rename(e.target.value)}
            onBlur={() => void flush()}
            placeholder={intl.formatMessage({
              id: "match.unnamedMatch",
              defaultMessage: "Unbenanntes Match",
            })}
            aria-label={intl.formatMessage({
              id: "match.renameLabel",
              defaultMessage: "Match umbenennen",
            })}
            className="-mx-1 min-w-[8ch] max-w-full rounded bg-transparent px-1 font-serif text-smycles-display-s leading-none placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-ui [field-sizing:content]"
          />
        )}
        <ModeIndicator mode={mode} />
        <SaveStatus
          saveState={editor.saveState}
          lastSavedAt={editor.lastSavedAt}
          onRetry={editor.retrySave}
        />
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="icon"
            size="sm"
            id="match-settings-open"
            type="button"
            onClick={() => setSettingsOpen(true)}
            title={intl.formatMessage({
              id: "match.workflow.settingsTitle",
              defaultMessage: "Match-Einstellungen",
            })}
            className="shrink-0"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </Button>
          {canDelete && (
            <Button
              id="match-delete-open"
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDeleteOpen(true)}
              className="tracking-[0.2em]"
              title={intl.formatMessage({
                id: "match.workflow.deleteTitle",
                defaultMessage: "Match löschen",
              })}
            >
              <FormattedMessage id="common.delete" defaultMessage="Löschen" />
            </Button>
          )}
        </div>
      </div>

      {/* Wofür dieses Match angelegt wurde. Steht direkt unter dem Titel, weil es
          zur Herkunft gehört und nicht zu den Schritten: wer ein Match aus einer
          Liste öffnet, soll ohne Umweg sehen, zu welchem Vorhaben es zählt. Die
          Verknüpfung selbst wird im Projekt gepflegt, deshalb hier nur der Weg
          dorthin und kein Lösen-Knopf. */}
      {editor.projects && editor.projects.length > 0 && (
        <div id="match-project-link" className="mt-2 text-smycles-meta text-ink-muted">
          <FormattedMessage
            id="match.workflow.belongsToProject"
            defaultMessage="Gehört zu {links}"
            values={{
              links: editor.projects.map((project, i) => (
                <span key={project.id}>
                  {i > 0 ? ", " : ""}
                  <Link
                    id={`match-project-link-${project.id}`}
                    to={`/project/${project.id}`}
                    className="transition hover:text-ink-accent"
                  >
                    {project.name ||
                      intl.formatMessage({
                        id: "match.workflow.unnamedProject",
                        defaultMessage: "Unbenanntes Projekt",
                      })}
                  </Link>
                </span>
              )),
            }}
          />
        </div>
      )}

      {myRole === "reader" && (
        <ReadOnlyBanner id="match-readonly-banner">
          <FormattedMessage
            id="match.readOnlyBanner"
            defaultMessage="Du kannst dieses Match nur ansehen. Bitte einen Admin oder Eigentümer:in um Schreibrechte."
          />
        </ReadOnlyBanner>
      )}

      <div className="mt-5">
        <Stepper steps={STEPS} activeIndex={stepIdx} mode={mode} onPick={(id) => setStepId(id)} />
      </div>

      {stepId === "challenge" && (
        <p className="mt-5 max-w-xl text-smycles-body text-ink-muted">
          <ChallengeSubtitle mode={mode} />
        </p>
      )}

      <div className="mt-6">
        {stepId === "challenge" && (
          <ChallengeStep
            data={data}
            update={update}
            editor={editor}
            sharedItem={editor.sharedItem}
            myRole={myRole}
            onNavigate={onNavigate}
            recruiting={describesRole(mode)}
          />
        )}
        {stepId === "people" &&
          (subject === "companies" ? (
            <CompanyPoolStep editor={editor} />
          ) : (
            <PeopleStep editor={editor} />
          ))}
        {/* The result step forks INSIDE ResultStep, not here. Both forks are about the same thing —
            "show me the result of this match" — and ResultStep is where the mode is already resolved
            for the save preview and the assistant frame; a second fork here would be a second place
            to keep in step. The pool step above is a different case: PeopleStep is a person finder
            by definition, and routing a company match into it would let a user fill a company pool
            with people. */}
        {stepId === "result" && (
          <ResultStep data={data} editor={editor} isReview={isReview} backSlot={backButton} />
        )}
      </div>

      {settingsOpen && (
        <MatchSettingsModal editor={editor} onClose={closeSettings} />
      )}

      {stepId !== "result" && (
        <FloatingActionRow>
          {backButton ?? <span />}
          {STEPS[stepIdx + 1] ? (
            <Button
              variant="primary"
              onClick={next}
              title={intl.formatMessage(
                {
                  id: "match.workflow.nextTo",
                  defaultMessage: "Weiter zu {label}",
                },
                {
                  label: STEPS[stepIdx + 1]
                    ? intl.formatMessage({ id: stepLabelId(STEPS[stepIdx + 1]!.id, mode) })
                    : "",
                },
              )}
              size="floating"
              className="pointer-events-auto tracking-[0.2em]"
            >
              {STEPS[stepIdx + 1]?.n}{" "}
              {STEPS[stepIdx + 1] ? (
                <FormattedMessage id={stepLabelId(STEPS[stepIdx + 1]!.id, mode)} />
              ) : null}{" "}
              →
            </Button>
          ) : null}
        </FloatingActionRow>
      )}

      <ConfirmDialog
        id="match-delete"
        open={confirmDeleteOpen}
        title={<FormattedMessage id="match.workflow.deleteConfirm.title" defaultMessage="Match löschen?" />}
        message={
          <FormattedMessage
            id="match.workflow.deleteConfirm.message"
            defaultMessage="Das Match wird endgültig aus genesis entfernt. Diese Aktion kann nicht rückgängig gemacht werden."
          />
        }
        confirmLabel={<FormattedMessage id="match.workflow.deleteConfirm.confirm" defaultMessage="Löschen" />}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
}

// Was die KI im Challenge-Step aus der Beschreibung ableitet, unterscheidet sich pro Modus, also
// steht hier ein erschoepfender switch statt einer Ternary-Kaskade, deren letzter Zweig jeden
// unbekannten Modus als Optimize gelesen hat. UNKNOWN zeigt nichts: jeder der drei Saetze waere
// eine Behauptung ueber einen Modus, den dieser Build nicht kennt.
function ChallengeSubtitle({ mode }: { mode: UiMatchMode }) {
  switch (mode) {
    case "OPTIMIZE":
      return (
        <FormattedMessage
          id="match.workflow.challengeSubtitle"
          defaultMessage="Beschreibe oben dein Projekt – die KI leitet daraus Team-Größe, Anforderungen und Powers ab. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
        />
      );
    case "RANK":
      return (
        <FormattedMessage
          id="match.workflow.challengeSubtitleRank"
          defaultMessage="Beschreibe oben die Rolle bzw. offene Stelle – die KI leitet daraus die Anforderungen ab, die ein:e Kandidat:in erfüllen soll. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
        />
      );
    case "ASSESS":
      return (
        <FormattedMessage
          id="match.workflow.challengeSubtitleNoTeamSize"
          defaultMessage="Beschreibe oben dein Projekt – die KI leitet daraus Anforderungen und Powers ab. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
        />
      );
    // Kein Satz ueber die KI: der Assistent leitet Personen-Anforderungen ab und kann die
    // Unternehmens-Kriterien nicht schreiben (getrennte Hierarchien, siehe design-codec), also wird hier
    // auch nichts versprochen. "Bedarf" ist das Wort der Modus-Karte.
    case "RANK_COMPANIES":
      return (
        <FormattedMessage
          id="match.workflow.challengeSubtitleRankCompanies"
          defaultMessage="Beschreibe oben deinen Bedarf. Darunter legst du fest, welche Anforderungen ein Unternehmen erfüllen soll."
        />
      );
    case UNKNOWN_MATCH_MODE:
      return null;
    default:
      return assertUnhandledMode(mode);
  }
}

// Read-only indicator — matchMode is immutable after creation (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4),
// so the former in-place toggle is now just a label of the chosen mode.
// No pill at all for a mode this build cannot name (matchModeLabelId → null): the label cascade
// this replaced ended in "Optimieren", i.e. it named the wrong mode with full confidence.
function ModeIndicator({ mode }: { mode: UiMatchMode }) {
  const intl = useIntl();
  const labelId = matchModeLabelId(mode);
  if (labelId === null) return null;
  return (
    <span
      id="match-mode-chip"
      className="rounded-full px-2.5 py-1 text-smycles-label uppercase tracking-[0.2em] ring-1 ring-ink/15 text-ink"
      title={intl.formatMessage({
        id: "match.workflow.modeIndicatorTitle",
        defaultMessage: "Mode (fixed at creation)",
      })}
    >
      <FormattedMessage id={labelId} />
    </span>
  );
}

function Stepper({
  steps,
  activeIndex,
  mode,
  onPick,
}: {
  steps: ReadonlyArray<{ n: string; id: StepId; label: string }>;
  activeIndex: number;
  mode: UiMatchMode;
  onPick: (id: StepId) => void;
}) {
  const last = steps.length - 1;
  return (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
      {steps.map((s, i) => {
        const state = i < activeIndex ? "done" : i === activeIndex ? "active" : "todo";
        const faintLine = "rgb(from currentColor r g b / 0.15)";
        const numColor =
          state === "active"
            ? "var(--context-accent)"
            : state === "done"
              ? "currentColor"
              : "rgb(from currentColor r g b / 0.35)";
        const align = i === 0 ? "items-start" : i === last ? "items-end" : "items-center";
        const labelClass =
          state === "active"
            ? "mt-3 font-serif text-smycles-display-m leading-none tracking-tight"
            : "mt-3 text-smycles-micro tracking-[0.25em]";
        const labelColor =
          state === "todo" ? "rgb(from currentColor r g b / 0.4)" : "currentColor";
        return (
          <button
            key={s.id}
            id={`match-step-${s.id}`}
            onClick={() => onPick(s.id)}
            aria-current={state === "active" ? "step" : undefined}
            // The cursor is the whole hover affordance, deliberately. Tailwind 4
            // dropped the default pointer on <button>, which is why these read as
            // static text. A tint or underline would have to be *added* to a
            // composition made only of hairlines and type — and the steps you can
            // actually click back to are already at full strength, so there is
            // nothing to brighten either. Pointer + focus ring, nothing drawn.
            // The active step gets no pointer: clicking it is a no-op.
            className={`flex flex-col rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${
              state === "active" ? "" : "cursor-pointer"
            } ${align}`}
          >
            <div className="flex w-full items-center">
              {i !== 0 && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex >= i ? "currentColor" : faintLine }}
                />
              )}
              <span
                className={`text-smycles-label font-medium tracking-wider ${i !== 0 ? "ml-3" : ""} ${
                  i !== last ? "mr-3" : ""
                }`}
                style={{ color: numColor }}
              >
                {s.n}
              </span>
              {i !== last && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex > i ? "currentColor" : faintLine }}
                />
              )}
            </div>
            <span className={labelClass} style={{ color: labelColor }}>
              <FormattedMessage id={stepLabelId(s.id, mode)} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
